import React from "react";
import {
    Clock,
    DollarSign,
    FileText,
    AlertCircle,
    Plus,
    Image as ImageIcon,
    FileText as FileIcon,
    Zap,
    Calendar,
    ArrowUpRight,
    Warehouse,
    ShieldAlert,
    Activity,
    ArrowRight,
    Sparkles,
    Send
} from "lucide-react";

// --- Mock Data ---

const MOCK_CATEGORIZED_INVOICES = [
    { id: 1001, vendor: "FreshFoods", amount: 1250.00, date: "2023-10-25", category: "Food & Beverage", status: "Processed" },
    { id: 1002, vendor: "DairyCo", amount: 430.50, date: "2023-10-26", category: "Food & Beverage", status: "Processed" },
    { id: 1003, vendor: "EcoPower", amount: 890.00, date: "2023-10-20", category: "Utilities", status: "Processed" },
    { id: 1004, vendor: "City Water", amount: 120.00, date: "2023-10-18", category: "Utilities", status: "Processed" },
    { id: 1005, vendor: "MarketMasters", amount: 1500.00, date: "2023-10-15", category: "Marketing", status: "Flagged" },
    { id: 1006, vendor: "PropertyLink", amount: 4500.00, date: "2023-10-01", category: "Rent & Lease", status: "Processed" },
    { id: 1007, vendor: "StaffDirect", amount: 3200.00, date: "2023-10-28", category: "Staff & Payroll", status: "Pending" },
];

const MOCK_SUPPLIERS = [
    { name: "FreshFoods", category: "Food & Beverage", totalSpend: 15400, trend: "+4.2%", invoices: 12, rating: 4.8, email: "orders@freshfoods.com", phone: "+44 20 7123 4567" },
    { name: "DairyCo", category: "Food & Beverage", totalSpend: 8200, trend: "+18.5%", invoices: 8, rating: 4.2, email: "billing@dairyco.co.uk", phone: "+44 20 8987 6543" },
    { name: "EcoPower", category: "Utilities", totalSpend: 4500, trend: "-2.1%", invoices: 4, rating: 4.5, email: "support@ecopower.uk", phone: "+44 20 3456 7890" },
    { name: "MarketMasters", category: "Marketing", totalSpend: 12000, trend: "+5.0%", invoices: 6, rating: 4.0, email: "hello@marketmasters.io", phone: "+44 20 5678 1234" },
    { name: "PropertyLink", category: "Rent & Lease", totalSpend: 54000, trend: "0.0%", invoices: 12, rating: 4.9, email: "rentals@propertylink.com", phone: "+44 20 9012 3456" },
];

const DASHBOARD_DATA_VARIANTS: Record<string, any> = {
    "This Month": {
        funnel: [
            { label: "Uploaded", value: 132, color: "bg-blue-100 text-blue-700" },
            { label: "Auto-processed", value: 124, color: "bg-emerald-100 text-emerald-700" },
            { label: "Needs review", value: 8, color: "bg-amber-100 text-amber-700" },
            { label: "Approved", value: 118, color: "bg-indigo-100 text-indigo-700" },
            { label: "Paid", value: 95, color: "bg-slate-100 text-slate-700" }
        ],
        trend: [12, 15, 8, 19, 21, 14, 25, 18, 22, 16, 28, 24, 20, 15],
        stats: {
            approvalNeeded: 8,
            processed: 145,
            receivable: 5500,
            payable: 1210,
            vat: { collected: 12400, paid: 8900, issues: 1 }
        },
        payables: [
            { label: "Due this week", value: 4500, color: "bg-blue-600" },
            { label: "Next 14 days", value: 8200, color: "bg-indigo-400" },
            { label: "Overdue", value: 1200, color: "bg-rose-500" },
        ],
        receivables: [
            { label: "0-30 Days", value: 12500, color: "bg-emerald-500", percent: 65 },
            { label: "31-60 Days", value: 4200, color: "bg-amber-500", percent: 22 },
            { label: "60+ Days", value: 1800, color: "bg-rose-500", percent: 13 },
        ],
        inventoryItems: [
            { id: 1, name: "Avocados (Case)", currentPrice: 45.50, lastPrice: 38.00, unit: "case" },
            { id: 2, name: "Ribeye Steaks", currentPrice: 18.90, lastPrice: 16.50, unit: "kg" },
            { id: 3, name: "Sourdough Loaves", currentPrice: 4.25, lastPrice: 3.50, unit: "unit" },
            { id: 4, name: "Heavy Cream", currentPrice: 6.50, lastPrice: 5.20, unit: "L" },
        ],
        categorySpendTrend: [
            { month: "May", items: [4500, 1200, 800] },
            { month: "Jun", items: [5200, 1100, 850] },
            { month: "Jul", items: [4800, 1300, 900] },
            { month: "Aug", items: [6500, 1250, 950] },
            { month: "Sep", items: [7200, 1350, 1000] },
            { month: "Oct", items: [8500, 1400, 1200] },
        ]
    },
    "Last Month": {
        funnel: [
            { label: "Uploaded", value: 98, color: "bg-blue-100 text-blue-700" },
            { label: "Auto-processed", value: 85, color: "bg-emerald-100 text-emerald-700" },
            { label: "Needs review", value: 13, color: "bg-amber-100 text-amber-700" },
            { label: "Approved", value: 82, color: "bg-indigo-100 text-indigo-700" },
            { label: "Paid", value: 78, color: "bg-slate-100 text-slate-700" }
        ],
        trend: [10, 8, 12, 11, 9, 15, 20, 18, 14, 12, 10, 8, 9, 11],
        stats: {
            approvalNeeded: 13,
            processed: 112,
            receivable: 4200,
            payable: 2800,
            vat: { collected: 10500, paid: 7200, issues: 0 }
        },
        payables: [
            { label: "Due this week", value: 3200, color: "bg-blue-600" },
            { label: "Next 14 days", value: 5400, color: "bg-indigo-400" },
            { label: "Overdue", value: 800, color: "bg-rose-500" },
        ],
        receivables: [
            { label: "0-30 Days", value: 9500, color: "bg-emerald-500", percent: 60 },
            { label: "31-60 Days", value: 3800, color: "bg-amber-500", percent: 25 },
            { label: "60+ Days", value: 2500, color: "bg-rose-500", percent: 15 },
        ],
        inventoryItems: [
            { id: 1, name: "Avocados (Case)", currentPrice: 40.50, lastPrice: 38.00, unit: "case" },
            { id: 2, name: "Ribeye Steaks", currentPrice: 17.90, lastPrice: 16.50, unit: "kg" },
            { id: 3, name: "Sourdough Loaves", currentPrice: 3.95, lastPrice: 3.50, unit: "unit" },
            { id: 4, name: "Heavy Cream", currentPrice: 5.80, lastPrice: 5.20, unit: "L" },
        ],
        categorySpendTrend: [
            { month: "Apr", items: [4000, 1000, 700] },
            { month: "May", items: [4500, 1200, 800] },
            { month: "Jun", items: [5200, 1100, 850] },
            { month: "Jul", items: [4800, 1300, 900] },
            { month: "Aug", items: [6500, 1250, 950] },
            { month: "Sep", items: [7200, 1350, 1000] },
        ]
    },
    "All Time": {
        funnel: [
            { label: "Uploaded", value: 1240, color: "bg-blue-100 text-blue-700" },
            { label: "Auto-processed", value: 1150, color: "bg-emerald-100 text-emerald-700" },
            { label: "Needs review", value: 90, color: "bg-amber-100 text-amber-700" },
            { label: "Approved", value: 1120, color: "bg-indigo-100 text-indigo-700" },
            { label: "Paid", value: 1080, color: "bg-slate-100 text-slate-700" }
        ],
        trend: [25, 30, 28, 35, 40, 38, 45, 42, 48, 50, 55, 52, 58, 60],
        stats: {
            approvalNeeded: 90,
            processed: 1240,
            receivable: 15400,
            payable: 8200,
            vat: { collected: 85000, paid: 62000, issues: 4 }
        },
        payables: [
            { label: "Due this week", value: 12000, color: "bg-blue-600" },
            { label: "Next 14 days", value: 25000, color: "bg-indigo-400" },
            { label: "Overdue", value: 4500, color: "bg-rose-500" },
        ],
        receivables: [
            { label: "0-30 Days", value: 45000, color: "bg-emerald-500", percent: 70 },
            { label: "31-60 Days", value: 12000, color: "bg-amber-500", percent: 18 },
            { label: "60+ Days", value: 8000, color: "bg-rose-500", percent: 12 },
        ],
        inventoryItems: [
            { id: 1, name: "Avocados (Case)", currentPrice: 52.50, lastPrice: 38.00, unit: "case" },
            { id: 2, name: "Ribeye Steaks", currentPrice: 21.00, lastPrice: 16.50, unit: "kg" },
            { id: 3, name: "Sourdough Loaves", currentPrice: 5.25, lastPrice: 3.50, unit: "unit" },
            { id: 4, name: "Heavy Cream", currentPrice: 8.50, lastPrice: 5.20, unit: "L" },
        ],
        categorySpendTrend: [
            { month: "May", items: [4500, 1200, 800] },
            { month: "Jun", items: [5200, 1100, 850] },
            { month: "Jul", items: [4800, 1300, 900] },
            { month: "Aug", items: [6500, 1250, 950] },
            { month: "Sep", items: [7200, 1350, 1000] },
            { month: "Oct", items: [8500, 1400, 1200] },
        ]
    }
};

const MOCK_PRICE_VOLATILITY = [
    { name: "Fresh Salmon", supplier: "FreshFoods", volatility: 8.4 },
    { name: "Electricity", supplier: "EcoPower", volatility: 6.2 },
    { name: "Cooking Oil", supplier: "FreshFoods", volatility: 4.8 },
];

// --- Sub-Components ---

function AgentTrigger({ topic, isAsking, onToggle, query, setQuery }: { topic: string, isAsking: boolean, onToggle: () => void, query: string, setQuery: (q: string) => void }) {
    return (
        <div className="relative">
            <button
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                className={`p-1.5 rounded-lg transition-colors ${isAsking ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:bg-gray-50 hover:text-purple-500'}`}
                title="Query Agent"
            >
                <Sparkles size={16} />
            </button>
            {isAsking && (
                <div className="absolute right-0 top-full mt-2 w-64 z-[100] animate-in fade-in slide-in-from-top-2 duration-300" onClick={e => e.stopPropagation()}>
                    <div className="bg-white border border-purple-100 rounded-xl p-3 shadow-xl ring-4 ring-purple-50/50">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles size={12} className="text-purple-600 animate-pulse" />
                            <span className="text-[10px] font-black text-purple-700 uppercase tracking-widest">AI Analyst</span>
                        </div>
                        <div className="flex gap-2">
                            <input
                                autoFocus
                                placeholder={`Verify ${topic}...`}
                                className="flex-1 bg-gray-50 border border-purple-50 rounded-lg px-3 py-2 text-xs font-medium text-gray-700 placeholder:text-gray-400 focus:ring-1 focus:ring-purple-400 outline-none"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (setQuery(""), onToggle())}
                            />
                            <button
                                onClick={() => (setQuery(""), onToggle())}
                                className="bg-purple-600 text-white p-2 rounded-lg hover:bg-purple-700 transition-colors"
                            >
                                <Send size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Card({
    title,
    children,
    className = "",
    headerAction,
    isCompact = false
}: {
    title?: string;
    children: React.ReactNode;
    className?: string;
    headerAction?: React.ReactNode;
    isCompact?: boolean;
}) {
    const [isAsking, setIsAsking] = React.useState(false);
    const [query, setQuery] = React.useState("");

    return (
        <div className={`bg-white rounded-xl border border-gray-100 shadow-sm ${isCompact ? 'p-3.5' : 'p-5'} flex flex-col transition-all duration-300 ${isAsking ? 'border-purple-200 ring-2 ring-purple-50' : ''} ${className}`}>
            {title && (
                <div className={`flex justify-between items-center ${isCompact ? 'mb-3' : 'mb-4'}`}>
                    <h3 className={`text-gray-900 font-bold tracking-tight ${isCompact ? 'text-sm' : 'text-base'}`}>{title}</h3>
                    <div className="flex items-center gap-3">
                        {headerAction}
                        <button
                            onClick={() => setIsAsking(!isAsking)}
                            className={`p-1.5 rounded-lg transition-colors ${isAsking ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:bg-gray-50 hover:text-purple-500'}`}
                            title="Query Agent"
                        >
                            <Sparkles size={16} />
                        </button>
                    </div>
                </div>
            )}
            <div className="flex-1 relative">
                {isAsking && (
                    <div className="absolute inset-x-0 -top-2 z-10 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 shadow-sm mb-4">
                            <div className="flex items-center gap-2">
                                <Sparkles size={12} className="text-purple-600 animate-pulse" />
                                <span className="text-[10px] font-black text-purple-700 uppercase tracking-widest">AI Analyst</span>
                            </div>
                            <div className="mt-2 flex gap-2">
                                <input
                                    autoFocus
                                    placeholder={`Ask about ${title}...`}
                                    className="flex-1 bg-white border border-purple-100 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 placeholder:text-gray-400 focus:ring-1 focus:ring-purple-400 outline-none"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && (setQuery(""), setIsAsking(false))}
                                />
                                <button
                                    onClick={() => (setQuery(""), setIsAsking(false))}
                                    className="bg-purple-600 text-white p-1.5 rounded-lg hover:bg-purple-700 transition-colors"
                                >
                                    <Send size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <div className={isAsking ? 'opacity-40 blur-[1px] pointer-events-none' : 'transition-all duration-300'}>
                    {children}
                </div>
            </div>
        </div>
    );
}

function FunnelSegment({ label, value, color, isLast, isCompact }: { label: string, value: number, color: string, isLast?: boolean, isCompact?: boolean }) {
    return (
        <div className="flex-1 flex items-center relative group">
            <div className={`flex-1 ${isCompact ? 'h-12 p-2' : 'h-16 p-3'} ${color} rounded-2xl flex flex-col justify-center transition-all group-hover:scale-[1.02] shadow-sm`}>
                <p className="text-[8px] font-bold uppercase tracking-widest opacity-70 mb-0.5">{label}</p>
                <div className="flex items-baseline gap-1">
                    <p className={`${isCompact ? 'text-lg' : 'text-xl'} font-black tracking-tight`}>{value}</p>
                    <p className="text-[8px] font-bold opacity-50">inv</p>
                </div>
            </div>
            {!isLast && (
                <div className="px-3 flex items-center text-gray-300">
                    <ArrowRight size={20} strokeWidth={3} />
                </div>
            )}
        </div>
    );
}

export function InvoicesView({ onUploadImage, onUploadDocument }: { onUploadImage?: () => void; onUploadDocument?: () => void }) {
    const [showUploadMenu, setShowUploadMenu] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState("Dashboard");
    const [selectedCategory] = React.useState("All");
    const [selectedSupplierCategory] = React.useState("All");
    const [dateFilter, setDateFilter] = React.useState("This Month");
    const [customDates, setCustomDates] = React.useState({ from: "", to: "" });
    const [askingCard, setAskingCard] = React.useState<string | null>(null);
    const [cardQuery, setCardQuery] = React.useState("");

    const currentDashboardData = React.useMemo(() => {
        const filter = dateFilter === "Custom" ? "This Month" : dateFilter;
        return DASHBOARD_DATA_VARIANTS[filter] || DASHBOARD_DATA_VARIANTS["All Time"];
    }, [dateFilter]);

    return (
        <div className="max-w-[1400px] mx-auto space-y-6 pb-12 font-sans text-slate-900">

            {/* Main Header Card */}
            <div className="bg-[#EFF8FF] p-10 rounded-[28px] border border-blue-50 flex items-center justify-between shadow-sm">
                <div>
                    <h1 className="text-3xl font-extrabold text-[#0F172A] tracking-tight mb-2">Invoice Management</h1>
                    <p className="text-slate-500 font-medium text-lg">
                        Track inflation, approve bills, and monitor cash flow.
                    </p>
                    {currentDashboardData.stats.vat.issues > 0 && (
                        <div className="mt-4 inline-flex items-center gap-2 bg-rose-100 text-rose-700 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border border-rose-200 animate-pulse">
                            <ShieldAlert size={14} />
                            VAT issues detected: {currentDashboardData.stats.vat.issues}
                        </div>
                    )}
                </div>
                <div className="relative">
                    <button
                        onClick={() => setShowUploadMenu(!showUploadMenu)}
                        className="bg-[#0284C7] hover:bg-[#0369A1] text-white px-8 py-3.5 rounded-full text-xs font-black uppercase tracking-widest shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                    >
                        <Plus size={16} strokeWidth={4} />
                        Upload
                    </button>
                    {showUploadMenu && (
                        <div className="absolute right-0 top-full mt-3 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 py-2 animate-in fade-in zoom-in-95 duration-200">
                            <button onClick={() => { setShowUploadMenu(false); onUploadDocument?.(); }} className="w-full px-5 py-3 text-left hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-3">
                                <FileIcon size={18} className="text-gray-400" /> Upload Document
                            </button>
                            <button onClick={() => { setShowUploadMenu(false); onUploadImage?.(); }} className="w-full px-5 py-3 text-left hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-3">
                                <ImageIcon size={18} className="text-gray-400" /> Upload Image
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-1 p-1 bg-gray-100/50 rounded-xl w-fit">
                {["Dashboard", "Management", "Suppliers"].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-8 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === tab
                            ? "bg-white text-blue-600 shadow-sm"
                            : "text-gray-500 hover:text-gray-900"
                            }`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {activeTab === "Dashboard" && (
                <div className="space-y-6 animate-in fade-in duration-500">
                    {/* Owner-level Alerts (Attention Needed Strip) */}
                    <div className="bg-slate-900 text-white rounded-[20px] p-5 shadow-lg flex flex-wrap items-center gap-6">
                        <div className="flex items-center gap-3 pr-6 border-r border-slate-700">
                            <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center text-slate-900 animate-pulse">
                                <AlertCircle size={24} strokeWidth={3} />
                            </div>
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Attention Needed</p>
                                <p className="text-sm font-bold">4 high-priority signals</p>
                            </div>
                        </div>
                        <div className="flex flex-1 items-center gap-8 overflow-x-auto scrollbar-hide">
                            <div className="flex items-center gap-3 whitespace-nowrap">
                                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                <p className="text-xs font-bold text-slate-200">£1,200 Overdue Receivable</p>
                            </div>
                            <div className="flex items-center gap-3 whitespace-nowrap">
                                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                                <p className="text-xs font-bold text-slate-200">{currentDashboardData.stats.vat.issues} VAT Anomaly Detected</p>
                            </div>
                            <div className="flex items-center gap-3 whitespace-nowrap">
                                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                <p className="text-xs font-bold text-slate-200">DairyCo Price Jump (+18%)</p>
                            </div>
                            <div className="flex items-center gap-3 whitespace-nowrap">
                                <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                                <p className="text-xs font-bold text-slate-200">5 Large Invoices unapproved</p>
                            </div>
                        </div>
                        <button className="bg-white/10 hover:bg-white/20 text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-colors">
                            Take Action
                        </button>
                    </div>

                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2">
                        <div>
                            <h2 className="text-2xl font-black text-gray-900 tracking-tight">Performance Summary</h2>
                            <p className="text-sm font-medium text-gray-500">Real-time financial analytics</p>
                        </div>
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                            {dateFilter === "Custom" && (
                                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-300">
                                    <input
                                        type="date"
                                        value={customDates.from}
                                        onChange={(e) => setCustomDates({ ...customDates, from: e.target.value })}
                                        className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-[10px] font-bold text-gray-600 outline-none focus:border-blue-300 transition-colors"
                                    />
                                    <span className="text-gray-400 font-bold text-[10px]">to</span>
                                    <input
                                        type="date"
                                        value={customDates.to}
                                        onChange={(e) => setCustomDates({ ...customDates, to: e.target.value })}
                                        className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-[10px] font-bold text-gray-600 outline-none focus:border-blue-300 transition-colors"
                                    />
                                </div>
                            )}
                            <div className="flex items-center gap-2 bg-white border border-gray-100 p-1 rounded-xl shadow-sm">
                                <div className="pl-2 pr-1 text-gray-400"><Calendar size={14} /></div>
                                {["This Month", "Last Month", "Custom", "All Time"].map((range) => (
                                    <button
                                        key={range}
                                        onClick={() => setDateFilter(range)}
                                        className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${dateFilter === range
                                            ? "bg-gray-900 text-white"
                                            : "text-gray-400 hover:text-gray-600"
                                            }`}
                                    >
                                        {range}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                        {/* 1. Approval Queue */}
                        <div className={`p-6 rounded-xl bg-white border shadow-sm flex flex-col h-full hover:shadow-md transition-all cursor-pointer group relative ${askingCard === 'approval' ? 'border-purple-200 ring-4 ring-purple-50' : 'border-gray-100'}`}>
                            <div className="absolute top-4 right-4 group-hover:opacity-100 transition-opacity">
                                <AgentTrigger topic="Needs Approval" isAsking={askingCard === 'approval'} onToggle={() => setAskingCard(askingCard === 'approval' ? null : 'approval')} query={cardQuery} setQuery={setCardQuery} />
                            </div>
                            <div className={`flex items-start justify-between ${askingCard === 'approval' ? 'opacity-40 blur-[1px]' : ''}`}>
                                <div>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 group-hover:text-[#DD6B20] transition-colors">Needs Approval</p>
                                    <p className="text-4xl font-extrabold text-[#DD6B20] mb-1">8</p>
                                    <p className="text-sm font-medium text-gray-400 mb-6">Invoices needing review</p>
                                </div>
                                <div className="p-3 rounded-xl bg-orange-50 text-[#DD6B20] group-hover:bg-[#DD6B20] group-hover:text-white transition-colors">
                                    <FileText size={28} />
                                </div>
                            </div>
                            <div className={`space-y-3 pt-4 border-t border-gray-50 mt-auto ${askingCard === 'approval' ? 'opacity-40 blur-[1px]' : ''}`}>
                                <div className="flex justify-between items-center text-xs">
                                    <span className="font-bold text-gray-600">Possible duplicate</span>
                                    <span className="bg-orange-100 text-orange-700 font-black px-2 py-0.5 rounded-full">2</span>
                                </div>
                                <div className="flex justify-between items-center text-xs">
                                    <span className="font-bold text-gray-600">VAT anomaly</span>
                                    <span className="bg-red-100 text-red-700 font-black px-2 py-0.5 rounded-full">1</span>
                                </div>
                            </div>
                        </div>

                        {/* 2. Global Volume */}
                        <div className={`p-6 rounded-xl bg-white border shadow-sm flex flex-col h-full hover:shadow-md transition-all cursor-pointer group relative ${askingCard === 'automation' ? 'border-purple-200 ring-4 ring-purple-50' : 'border-gray-100'}`}>
                            <div className="absolute top-4 right-4 group-hover:opacity-100 transition-opacity">
                                <AgentTrigger topic="Global Volume" isAsking={askingCard === 'automation'} onToggle={() => setAskingCard(askingCard === 'automation' ? null : 'automation')} query={cardQuery} setQuery={setCardQuery} />
                            </div>
                            <div className={`flex items-start justify-between mb-4 ${askingCard === 'automation' ? 'opacity-40 blur-[1px]' : ''}`}>
                                <div>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 group-hover:text-purple-600 transition-colors">Global Volume</p>
                                    <p className="text-4xl font-extrabold text-purple-600 mb-1">145</p>
                                    <p className="text-sm font-medium text-gray-400">Invoices processed</p>
                                </div>
                                <div className="p-3 rounded-xl bg-purple-50 text-purple-600 group-hover:bg-purple-600 group-hover:text-white transition-colors">
                                    <Zap size={28} />
                                </div>
                            </div>
                            <div className="mt-auto">
                                <div className="flex justify-between items-end mb-2">
                                    <span className="text-xs font-bold text-gray-700">94% automated</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                    <div className="bg-purple-500 h-2" style={{ width: '94%' }}></div>
                                </div>
                            </div>
                        </div>

                        {/* 3. Accounts Receivable */}
                        <div className={`p-6 rounded-xl bg-white border shadow-sm flex flex-col h-full hover:shadow-md transition-all cursor-pointer group relative ${askingCard === 'receivable' ? 'border-purple-200 ring-4 ring-purple-50' : 'border-gray-100'}`}>
                            <div className="absolute top-4 right-4 group-hover:opacity-100 transition-opacity">
                                <AgentTrigger topic="Accounts Receivable" isAsking={askingCard === 'receivable'} onToggle={() => setAskingCard(askingCard === 'receivable' ? null : 'receivable')} query={cardQuery} setQuery={setCardQuery} />
                            </div>
                            <div className={`flex items-start justify-between mb-2 ${askingCard === 'receivable' ? 'opacity-40 blur-[1px]' : ''}`}>
                                <div>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 group-hover:text-blue-600 transition-colors">Accounts Receivable</p>
                                    <p className="text-4xl font-extrabold text-[#0F172A] mb-1">£5,500</p>
                                    <p className="text-sm font-medium text-gray-400">Outstanding</p>
                                </div>
                                <div className="p-3 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                    <DollarSign size={28} />
                                </div>
                            </div>
                        </div>

                        {/* 4. Accounts Payable */}
                        <div className={`p-6 rounded-xl bg-white border shadow-sm flex flex-col h-full hover:shadow-md transition-all cursor-pointer group relative ${askingCard === 'payable' ? 'border-purple-200 ring-4 ring-purple-50' : 'border-gray-100'}`}>
                            <div className="absolute top-4 right-4 group-hover:opacity-100 transition-opacity">
                                <AgentTrigger topic="Accounts Payable" isAsking={askingCard === 'payable'} onToggle={() => setAskingCard(askingCard === 'payable' ? null : 'payable')} query={cardQuery} setQuery={setCardQuery} />
                            </div>
                            <div className={`flex items-start justify-between mb-2 ${askingCard === 'payable' ? 'opacity-40 blur-[1px]' : ''}`}>
                                <div>
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 group-hover:text-slate-600 transition-colors">Accounts Payable</p>
                                    <p className="text-4xl font-extrabold text-[#0F172A] mb-1">£1,210</p>
                                    <p className="text-sm font-medium text-gray-400">Approved</p>
                                </div>
                                <div className="p-3 rounded-xl bg-slate-50 text-slate-600 group-hover:bg-slate-600 group-hover:text-white transition-colors">
                                    <Clock size={28} />
                                </div>
                            </div>
                        </div>

                        {/* 5. Margin Impact */}
                        <div className={`p-6 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md flex flex-col h-full hover:shadow-lg transition-all cursor-pointer group relative ${askingCard === 'margin' ? 'ring-4 ring-emerald-100' : ''}`}>
                            <div className="absolute top-4 right-4 text-white/40 group-hover:text-white/100 transition-colors">
                                <AgentTrigger topic="Margin & Runway" isAsking={askingCard === 'margin'} onToggle={() => setAskingCard(askingCard === 'margin' ? null : 'margin')} query={cardQuery} setQuery={setCardQuery} />
                            </div>
                            <div className={`flex flex-col h-full text-white ${askingCard === 'margin' ? 'opacity-40 blur-[1px]' : ''}`}>
                                <p className="text-[11px] font-black uppercase tracking-widest text-emerald-100 mb-4">Margin Impact</p>
                                <div className="mb-4">
                                    <p className="text-4xl font-extrabold mb-1">-2.1%</p>
                                    <p className="text-xs font-medium text-emerald-100 italic">Gross Margin Reduction</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        <div className="lg:col-span-3 space-y-6">
                            <Card title="Invoice Processing Funnel" isCompact={true}>
                                <div className="flex gap-2">
                                    {currentDashboardData.funnel.map((f: any, i: number) => (
                                        <FunnelSegment key={f.label} label={f.label} value={f.value} color={f.color} isLast={i === currentDashboardData.funnel.length - 1} isCompact={true} />
                                    ))}
                                </div>
                            </Card>

                            <Card title="Cost Intelligence & Signals" isCompact={true}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Inflation Monitor</p>
                                        <div className="space-y-0.5">
                                            {currentDashboardData.inventoryItems.map((item: any) => (
                                                <div key={item.id} className="grid grid-cols-4 px-2 py-1 border-b border-gray-50 hover:bg-gray-50 items-center">
                                                    <div className="col-span-2 font-bold text-gray-900 text-[10px]">{item.name}</div>
                                                    <div className="text-right text-gray-500 font-medium text-[9px]">£{item.lastPrice.toFixed(2)}</div>
                                                    <div className="text-right font-black text-red-600 text-[9px]">+{Math.round(((item.currentPrice - item.lastPrice) / item.lastPrice) * 100)}%</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Volatility Signals</p>
                                        <div className="space-y-2">
                                            {MOCK_PRICE_VOLATILITY.slice(0, 2).map((item, i) => (
                                                <div key={i} className="flex items-center justify-between p-2 rounded-xl bg-gray-50 border border-gray-100">
                                                    <div className="flex items-center gap-2">
                                                        <Activity size={14} className="text-amber-500" />
                                                        <div>
                                                            <p className="text-[10px] font-bold text-gray-900">{item.name}</p>
                                                            <p className="text-[8px] font-bold text-gray-400 uppercase">{item.supplier}</p>
                                                        </div>
                                                    </div>
                                                    <p className="text-[10px] font-black text-red-600">±{item.volatility}%</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <Card title="Spend by Category">
                                    <div className="h-48 flex items-end justify-between gap-2 px-2">
                                        {currentDashboardData.categorySpendTrend.map((d: any, i: number) => (
                                            <div key={i} className="flex-1 flex flex-col items-center gap-2 h-full">
                                                <div className="w-full flex flex-col justify-end gap-0.5 flex-1">
                                                    <div className="w-full bg-blue-500" style={{ height: `${(d.items[0] / 10000) * 100}%` }}></div>
                                                    <div className="w-full bg-indigo-400" style={{ height: `${(d.items[1] / 10000) * 100}%` }}></div>
                                                    <div className="w-full bg-emerald-400" style={{ height: `${(d.items[2] / 10000) * 100}%` }}></div>
                                                </div>
                                                <span className="text-[9px] font-black text-gray-400 uppercase">{d.month}</span>
                                            </div>
                                        ))}
                                    </div>
                                </Card>

                                <Card title="VAT Position Reconciliation">
                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Rate</p>
                                            <p className="text-xl font-black text-slate-900">20.0%</p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Gap</p>
                                            <p className="text-xl font-black text-rose-600">£{currentDashboardData.stats.vat.issues * 450}</p>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <Card title="Quick Actions" isCompact={true}>
                                <div className="space-y-1.5">
                                    <button className="w-full py-2 px-3 rounded-xl bg-blue-600 text-white text-[10px] font-bold shadow-sm hover:shadow-md transition-all flex items-center justify-between group">
                                        Batch Approve
                                        <ArrowRight size={10} className="group-hover:translate-x-1" />
                                    </button>
                                    <button className="w-full py-2 px-3 rounded-xl bg-white border border-gray-200 text-gray-700 text-[10px] font-bold hover:border-blue-400 transition-all text-left">
                                        Send Reminders
                                    </button>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "Management" && (
                <div className="space-y-6 animate-in fade-in duration-500">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card title="Category Distribution">
                            <div className="flex flex-col items-center justify-center h-48">
                                <div className="relative w-36 h-36">
                                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                                        <circle cx="18" cy="18" r="15.915" fill="none" stroke="#f1f5f9" strokeWidth="4" />
                                        <circle cx="18" cy="18" r="15.915" fill="none" stroke="#2563eb" strokeWidth="4" strokeDasharray="45 55" />
                                        <circle cx="18" cy="18" r="15.915" fill="none" stroke="#818cf8" strokeWidth="4" strokeDasharray="25 75" strokeDashoffset="-45" />
                                    </svg>
                                </div>
                            </div>
                        </Card>
                        <Card title="Spend Velocity">
                            <div className="flex items-end justify-between h-48 gap-1 px-1">
                                {[45, 60, 32, 85, 42, 55, 90, 38, 65, 72, 48, 88].map((val, i) => (
                                    <div key={i} className="flex-1 bg-blue-100 hover:bg-blue-600 transition-all rounded-t-sm" style={{ height: `${val}%` }}></div>
                                ))}
                            </div>
                        </Card>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Vendor</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {MOCK_CATEGORIZED_INVOICES.filter(inv => selectedCategory === "All" || inv.category === selectedCategory).map(invoice => (
                                    <tr key={invoice.id} className="hover:bg-gray-50/50 transition-colors cursor-pointer">
                                        <td className="px-6 py-4 font-bold text-gray-900">{invoice.vendor}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500">{invoice.category}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500">{invoice.date}</td>
                                        <td className="px-6 py-4 font-black text-gray-900">£{invoice.amount.toLocaleString()}</td>
                                        <td className="px-6 py-4 text-right">
                                            <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${invoice.status === 'Processed' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                                                {invoice.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === "Suppliers" && (
                <div className="space-y-6 animate-in fade-in duration-500">
                    <div className="grid grid-cols-1 gap-4">
                        {MOCK_SUPPLIERS.filter(s => selectedSupplierCategory === "All" || s.category === selectedSupplierCategory).map((supplier) => (
                            <div key={supplier.name} className="bg-white border border-gray-100 rounded-[20px] p-6 hover:shadow-md transition-all flex flex-col md:flex-row md:items-center justify-between group">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                        <Warehouse size={28} />
                                    </div>
                                    <div>
                                        <h3 className="font-black text-gray-900 text-lg">{supplier.name}</h3>
                                        <p className="text-[11px] font-bold text-gray-400 uppercase">{supplier.category}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 flex-1 md:px-12">
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Spend</p>
                                        <p className="text-base font-black text-gray-900">£{supplier.totalSpend.toLocaleString()}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Trend</p>
                                        <p className={`text-base font-black ${supplier.trend.startsWith('+') ? 'text-red-600' : 'text-emerald-600'}`}>{supplier.trend}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Rating</p>
                                        <p className="text-base font-black text-gray-900">{supplier.rating} ★</p>
                                    </div>
                                </div>
                                <button className="flex items-center gap-2 text-xs font-black uppercase text-[#0284C7] hover:bg-blue-50 px-4 py-2 rounded-lg transition-all">
                                    Analyze <ArrowUpRight size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
